// Copyright 2023, mochabug AB
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { fromJsonString, toJson } from '@bufbuild/protobuf';
import { ValueSchema } from '@bufbuild/protobuf/wkt';
import Ajv from 'ajv/dist/jtd.js';
import chalk from 'chalk';
import fg from 'fast-glob';
import fs from 'fs';
import { mkdirp } from 'mkdirp';
import path from 'path';
import { fileURLToPath } from 'url';
import { JTDSchemaSchema } from './genproto/mochabugapis/adapt/graph/jtd_schema_pb';
import { SignalDescriptor } from './genproto/mochabugapis/adapt/graph/signal_descriptor_pb';
import {
  VertexMetadata,
  VertexMetadataSchema
} from './genproto/mochabugapis/adapt/graph/vertex_metadata_pb';
import {
  Manifest,
  ManifestSchema
} from './genproto/mochabugapis/adapt/plugins/v1/manifest_pb';
import { Oauth2GrantType } from './genproto/mochabugapis/adapt/plugins/v1/oauth2_service_pb';
import { ServiceDefinition } from './genproto/mochabugapis/adapt/plugins/v1/service_definition_pb';
import { VertexSchema } from './genproto/mochabugapis/adapt/plugins/v1/vertex_pb';

// Create an Ajv instance configured for JTD
const ajv = new Ajv();

export function readAsset(name: string): string {
  return fs
    .readFileSync(
      path.join(
        path.dirname(fileURLToPath(import.meta.url)),
        '..',
        'assets',
        name
      )
    )
    .toString();
}

export function writeFile(filePath: string, content: string) {
  const dir = path.dirname(filePath);
  mkdirp.sync(dir);
  fs.writeFileSync(filePath, content);
}

export async function readManifest(
  filePath: string,
  src: string
): Promise<Manifest | null> {
  try {
    const baseDir = path.dirname(filePath);
    if (!fs.existsSync(filePath)) {
      throw new Error(
        `Invalid command: Manifest file not found at ${filePath}.`
      );
    }

    const manifestData = fs.readFileSync(filePath, 'utf-8');
    const manifest = fromJsonString(ManifestSchema, manifestData);

    const verticesPath = path.join(baseDir, src);
    if (!fs.existsSync(verticesPath)) {
      throw new Error(`Directory does not exist: ${verticesPath}`);
    }
    if (!fs.lstatSync(verticesPath).isDirectory()) {
      throw new Error(`Path exists but is not a directory: ${verticesPath}`);
    }

    const vertexFiles = fg
      .sync('**/vertex.json', {
        cwd: verticesPath,
        absolute: false
      })
      .map((f) => path.join(verticesPath, f));
    if (vertexFiles.length === 0) {
      throw new Error(
        `No vertex files found in the directory: ${verticesPath}. Please ensure that the directory contains at least one vertex file.`
      );
    }

    // Load and adjust the vertex files.
    manifest.vertices = vertexFiles.map((vertexFile) => {
      const vertex = fromJsonString(
        VertexSchema,
        fs.readFileSync(vertexFile, 'utf-8')
      );

      // Adjust metadata.json path.
      vertex.metadata = vertex.metadata
        ? path.join(path.dirname(vertexFile), vertex.metadata)
        : path.join(path.dirname(vertexFile), 'metadata.json');

      // Adjust paths for config and logo if they exist.
      if (vertex.config) {
        vertex.config = path.join(path.dirname(vertexFile), vertex.config);
      }
      if (vertex.logo) {
        vertex.logo = path.join(path.dirname(vertexFile), vertex.logo);
      }
      return vertex;
    });

    // Validate service references in each vertex.
    for (const v of manifest.vertices) {
      for (const u of v.userServices) {
        if (u.binding.case === 'service') {
          if (
            !manifest.serviceDefinitions.find((s) => s.name === u.binding.value)
          ) {
            throw new Error(
              `Service not found: ${u.binding.value} in vertex: ${v.name}`
            );
          }
        } else if (u.binding.case === 'oneOf') {
          for (const opt of u.binding.value.options) {
            if (!manifest.serviceDefinitions.find((s) => s.name === opt)) {
              throw new Error(`Service not found: ${opt} in vertex: ${v.name}`);
            }
          }
        } else {
          throw new Error(
            `Invalid binding case: ${u.binding.case} in vertex: ${v.name}`
          );
        }
      }
    }

    // Validate the system service definiton binding
    for (const u of manifest.systemServices) {
      if (u.binding.case === 'service') {
        if (
          !manifest.serviceDefinitions.find((s) => s.name === u.binding.value)
        ) {
          throw new Error(
            `Service not found: ${u.binding.value} in system services`
          );
        }
      } else if (u.binding.case === 'oneOf') {
        for (const opt of u.binding.value.options) {
          if (!manifest.serviceDefinitions.find((s) => s.name === opt)) {
            throw new Error(`Service not found: ${opt} in system services`);
          }
        }
      } else {
        throw new Error(
          `Invalid binding case: ${u.binding.case} in system services`
        );
      }
    }

    // Validate the vertex metadata schemas.
    for (const v of manifest.vertices) {
      const vertexMetadata = fromJsonString(
        VertexMetadataSchema,
        fs.readFileSync(v.metadata, 'utf-8')
      );
      await validateVertexConfigMetadata(
        vertexMetadata,
        `Vertex file: ${v.metadata}`
      );
    }

    // Make sure that code flow is configured correctly
    validateOauthCodeFlow(manifest);

    // Validate default values in service definitions.
    for (const srv of manifest.serviceDefinitions) {
      await validateLoadServiceDefinition(baseDir, srv);
    }

    // TODO: When protovalidate for typescript is released perform it here

    return manifest;
  } catch (error: any) {
    console.error(
      chalk.bgRed.white.bold('\n FATAL ERROR: Manifest loading failed! \n')
    );
    console.error(chalk.redBright(error.message));
    process.exit(1);
  }
}

export function labelize(val: string): string {
  return (val.charAt(0).toUpperCase() + val.substring(1)).replace('_', ' ');
}

export function validateOauthCodeFlow(manifest: Manifest) {
  const codeDefs: ServiceDefinition[] = [];
  for (const srv of manifest.serviceDefinitions) {
    if (
      srv.type.case !== 'oauth2' ||
      srv.type.value.grantType !== Oauth2GrantType.AUTHORIZATION_CODE
    ) {
      continue;
    }
    codeDefs.push(srv);
  }

  // Now, make sure that all of the references is non-optional in the manifest.systemServices to any of the codeDefs
  // Furthermore, we need to make sure that the codeDefs does not appear in any oneOf binding in the systemServices either
  const codeDefNames = codeDefs.map((s) => s.name);
  for (const srv of manifest.systemServices) {
    if (
      srv.optional &&
      srv.binding.case === 'service' &&
      codeDefNames.includes(srv.binding.value)
    ) {
      throw new Error(
        `Service ${srv.binding.value} is using the OAuth2 code flow, it must be non-optional in the system services`
      );
    } else if (srv.binding.case === 'oneOf') {
      for (const opt of srv.binding.value.options) {
        if (codeDefNames.includes(opt)) {
          throw new Error(
            `Service binding oneOf '${opt}' is using the OAuth2 code flow, it must be non-optional in the system services`
          );
        }
      }
    }
  }

  // Now, we need to validate that if a codeDef is used in any vertex, it must also exist in the systemServices
  for (const vertex of manifest.vertices) {
    for (const srv of vertex.userServices) {
      if (
        srv.binding.case === 'service' &&
        codeDefNames.includes(srv.binding.value)
      ) {
        if (
          !manifest.systemServices.find(
            (s) =>
              s.binding.case === 'service' &&
              s.binding.value === srv.binding.value
          )
        ) {
          throw new Error(
            `Service ${srv.binding.value} is using the OAuth2 code flow, it must be in the system services`
          );
        }
      } else if (srv.binding.case === 'oneOf') {
        for (const opt of srv.binding.value.options) {
          if (codeDefNames.includes(opt)) {
            if (
              !manifest.systemServices.find(
                (s) => s.binding.case === 'service' && s.binding.value === opt
              )
            ) {
              throw new Error(
                `Service binding oneOf '${opt}' is using the OAuth2 code flow, it must be in the system services`
              );
            }
          }
        }
      }
    }
  }
}

// Service definitions can have files, these needs to be loaded and validated
export async function validateLoadServiceDefinition(
  basePath: string,
  srv: ServiceDefinition
) {
  switch (srv.type.case) {
    case 'variable': {
      // Validate the JTD schema as well as the default value
      try {
        const schema = toJson(JTDSchemaSchema, srv.type.value.schema!);
        await ajv.validateSchema(schema, true);
        if (srv.type.value.default) {
          if (
            !ajv.validate(schema, toJson(ValueSchema, srv.type.value.default))
          ) {
            throw new Error(
              `Default value is incorrect: ${ajv.errorsText(ajv.errors)}`
            );
          }
        }
      } catch (error: any) {
        throw new Error(
          `Validation failed for service definition '${
            srv.name
          }': ${formatErrorMessage(error.message)}`
        );
      }
      break;
    }
  }
}

/**
 * Formats an error message by splitting it on commas and indenting each part.
 */
function formatErrorMessage(message: string): string {
  return message
    .split(/,\s*/g)
    .map((line) => `  - ${line}`)
    .join('\n');
}

export async function validateVertexConfigMetadata(
  metadata: VertexMetadata,
  context: string = 'Vertex'
) {
  // Validate the exchanges
  for (let exch of metadata.exchanges) {
    for (let transmitter of exch.transmitters) {
      try {
        await validateSignalDescriptors(
          transmitter.signals,
          `${context} > Procedure ${exch.name} > Transmitter ${transmitter.name}`
        );
      } catch (err: any) {
        throw new Error(
          `Error validating signals in ${context} > Procedure ${
            exch.name
          } > Transmitter ${transmitter.name}:\n${formatErrorMessage(
            err.message
          )}`
        );
      }
    }
    for (let receiver of exch.receivers) {
      try {
        await validateSignalDescriptors(
          receiver.signals,
          `${context} > Procedure ${exch.name} > Receiver ${receiver.name}`
        );
      } catch (err: any) {
        throw new Error(
          `Error validating signals in ${context} > Procedure ${
            exch.name
          } > Receiver ${receiver.name}:\n${formatErrorMessage(err.message)}`
        );
      }
    }
  }

  // Validate top-level transmitters
  for (let transmitter of metadata.transmitters) {
    try {
      await validateSignalDescriptors(
        transmitter.signals,
        `${context} > Top-level Transmitter ${transmitter.name}`
      );
    } catch (err: any) {
      throw new Error(
        `Error validating signals in ${context} > Top-level Transmitter ${
          transmitter.name
        }:\n${formatErrorMessage(err.message)}`
      );
    }
  }

  // Validate top-level receivers
  for (let receiver of metadata.receivers) {
    try {
      await validateSignalDescriptors(
        receiver.bindings.map((b) => b.descriptor!),
        `${context} > Top-level Receiver ${receiver.name}`
      );
    } catch (err: any) {
      throw new Error(
        `Error validating signals in ${context} > Top-level Receiver ${
          receiver.name
        }:\n${formatErrorMessage(err.message)}`
      );
    }
  }
}

export async function validateSignalDescriptors(
  descs: SignalDescriptor[],
  context: string = 'Signal'
) {
  for (let desc of descs) {
    try {
      await ajv.validateSchema(toJson(JTDSchemaSchema, desc.schema!), true);
    } catch (error: any) {
      throw new Error(
        `Validation failed for signal descriptor ${
          desc.name
        } in context (${context}):\n${formatErrorMessage(error.message)}`
      );
    }
  }
}
